import { takeLatest, call, put, select } from 'redux-saga/effects';

// Helpers
import { googleAuth, auth } from 'services/firebase';
import { signOut, signInWithEmailAndPassword } from 'firebase/auth';
import {
  getStorageLastPath,
  setStorageUserClaims,
  setStorageUserToken,
} from 'helpers/ticketSales';

// Services
import { appUser } from 'services/apiDigitalTicket/users';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import ENVIRONMENTS from 'constants/enviroments';
import PATH from 'constants/basePath';
import DOCS from 'constants/documents';

// Actions
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as ticketSalesActions from './actions';
import { publicStoreTypes } from './types';
import { getAppUserSuccess } from 'store/ducks/users/actions';
import { LIST_ERROR_CODES } from 'constants/errorCodes';

function* ticketSalesLogin({
  payload,
}: ReturnType<typeof ticketSalesActions.ticketSalesLoginRequest>): any {
  try {
    // !!! For security, always clear others datas and logins
    yield call(() => signOut(auth));

    let errCode = '';
    if (payload) {
      yield put(DialogsActions.setOpenLoading());
      yield call(() =>
        signInWithEmailAndPassword(auth, payload.email, payload.password).catch(
          (err) => {
            errCode = err.code;
          },
        ),
      );

      if (errCode) {
        if (
          errCode === 'auth/user-not-found' ||
          errCode === 'auth/wrong-password'
        ) {
          yield put(
            ticketSalesActions.ticketSalesLoginFailure({
              key: errCode === 'auth/user-not-found' ? 'email' : 'password',
              message: errorMessagersList(errCode),
            }),
          );
        } else {
          yield put(
            SnackbarsActions.setOpen({
              open: true,
              type: 'error',
              message: errorMessagersList(errCode),
            }),
          );
        }
      }
    } else {
      yield googleAuth();
    }

    const tokenResult = yield auth.currentUser!.getIdTokenResult();
    yield put(ticketSalesActions.ticketSalesLoginSuccess());

    if (tokenResult.token && tokenResult.claims) {
      setStorageUserToken(tokenResult.token);

      const parseClaims = tokenResult?.claims;
      const response = yield call(appUser.get, {
        params: {
          transportOperatorId: ENVIRONMENTS.OPERATOR_ID,
          id: parseClaims?.sub,
          authUserId: parseClaims?.sub,
          _includes: 'document',
        },
      });

      if (response.status === 200) {
        setStorageUserClaims(
          JSON.stringify({
            ...tokenResult,
            document:
              response.data?.UserDocuments.find(
                (e: { type: number }) => e.type === DOCS.DOCUMENT_CPF.TYPE,
              )?.document || null,
          }),
        );
        yield put(
          getAppUserSuccess({
            appUserData: response.data,
          }),
        );
        yield select((state) => state.publicStore.step);

        if (getStorageLastPath()) {
          window.location.replace(getStorageLastPath());
        } else {
          window.location.replace(PATH.TICKET_RESERVATION_WEBSITE.BASE_URL);
        }
      }
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - authLogin - error', error);
    yield put(DialogsActions.setClose());

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(
      SnackbarsActions.setSecondOpen({
        open: true,
        type: 'warning',
        message: 'Cadastre o e-mail para continuar.',
      }),
    );
    if (LIST_ERROR_CODES[internalCode as keyof typeof LIST_ERROR_CODES]) {
      yield put(ticketSalesActions.setIsCreateProfile(true));
    }
  }
}

export const publicStoreSagas = [
  takeLatest(publicStoreTypes.TICKET_SALES_LOGIN_REQUEST, ticketSalesLogin),
];
